import React, { useRef, useState } from "react";
import FileList from "./FileList";
import { FOLDER_TYPES } from "./FolderGrid";
import { FolderSvg } from "./FolderCard";
import { buildEncryptedUpload } from "../../utils/crypto";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function FolderDetail({
  category,
  files,
  token,
  plan = { name: "Basic", maxFilesPerFolder: 5, unlimited: false },
  onBack,
  onUploadComplete,
  nominees = [],
}) {
  const ft =
    FOLDER_TYPES.find(
      (f) => f.label.toLowerCase() === category.toLowerCase()
    ) || FOLDER_TYPES[0];

  const categoryFiles = files.filter(
    (f) => (f.category || "Personal") === category
  );

  const atFolderLimit = !plan.unlimited && categoryFiles.length >= plan.maxFilesPerFolder;

  // ── Upload state ─────────────────────────────────────────────────────────
  const [showUpload,  setShowUpload]  = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [progress,    setProgress]    = useState(0);
  const [uploadDone,  setUploadDone]  = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef();

  const resetUpload = () => {
    setSelectedFile(null);
    setProgress(0);
    setUploadDone(false);
    setUploadError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setSelectedFile(f);
      setUploadDone(false);
      setUploadError("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setProgress(0);
    setUploadError("");

    try {
      // Encrypt in the browser — plaintext never leaves the device.
      const formData = await buildEncryptedUpload(selectedFile, { category });

      // Simulate progress while waiting (fetch doesn't expose real upload progress)
      const interval = setInterval(() => {
        setProgress((p) => (p < 85 ? p + 5 : p));
      }, 200);

      const res = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      clearInterval(interval);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || data?.error || "Upload failed");
      }

      setProgress(100);
      setUploadDone(true);
      resetUpload();

      // Refresh file list in parent
      if (onUploadComplete) onUploadComplete();

      // Auto-close panel after success
      setTimeout(() => {
        setUploadDone(false);
        setShowUpload(false);
      }, 2000);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="px-8 py-10 flex flex-col min-h-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={onBack}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-dark-border text-primary hover:bg-white/5 transition text-xl font-bold"
          aria-label="Back to vault"
        >
          ‹
        </button>
        <FolderSvg color={ft.color} size={36} />
        <div>
          <h1 className="text-2xl font-bold leading-tight">{category}</h1>
          <p className={`text-sm ${atFolderLimit ? "text-amber-400" : "text-gray-400"}`}>
            {plan.unlimited
              ? `${categoryFiles.length} file${categoryFiles.length !== 1 ? "s" : ""}`
              : `${categoryFiles.length} / ${plan.maxFilesPerFolder} files`}
            {atFolderLimit && " — folder limit reached"}
          </p>
        </div>
      </div>

      {/* Files */}
      {categoryFiles.length === 0 && !showUpload ? (
        <div className="text-center py-16 bg-dark-card border border-dark-border rounded-2xl mb-6">
          <div className="text-5xl mb-3">📂</div>
          <p className="text-gray-400 text-sm">No files in this folder yet.</p>
        </div>
      ) : (
        <div className="mb-6">
          {/* onUploadComplete is really "refetch everything" (see MyFiles.jsx's
              load()) — reused here so a delete refreshes the list the same way
              a successful upload already does. */}
          <FileList files={categoryFiles} token={token} nominees={nominees} onDeleted={onUploadComplete} />
        </div>
      )}

      {/* ── Upload panel (shown when Add Content is clicked) ── */}
      {showUpload && (
        <div className="bg-dark-card border border-dark-border rounded-2xl p-6 mb-4 transition-all">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-white">Add File to "{category}"</h3>
            <button
              onClick={() => { setShowUpload(false); resetUpload(); }}
              className="text-gray-500 hover:text-white transition text-lg leading-none"
              aria-label="Close upload panel"
            >
              ✕
            </button>
          </div>

          {/* Drop / select area */}
          <label
            htmlFor="folder-file-input"
            className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-dark-border rounded-xl py-10 cursor-pointer hover:border-primary transition mb-4"
          >
            <div className="text-4xl">📁</div>
            <p className="text-sm text-gray-400">
              {selectedFile ? selectedFile.name : "Click to select a file"}
            </p>
            {selectedFile && (
              <p className="text-xs text-gray-500">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            )}
            <input
              id="folder-file-input"
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              disabled={uploading}
            />
          </label>

          {/* Progress bar */}
          {uploading && (
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Uploading…</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-dark-bg rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Success */}
          {uploadDone && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm mb-4">
              <span>✓</span> File uploaded successfully!
            </div>
          )}

          {/* Error */}
          {uploadError && (
            <div className="text-red-400 text-sm mb-4">{uploadError}</div>
          )}

          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-dark transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {uploading ? `Uploading… ${progress}%` : "Upload File"}
          </button>
        </div>
      )}

      {/* ── Add Content bar — hidden once the folder hits its plan limit, so
             a user can't select a file only to have the upload rejected. ── */}
      {atFolderLimit && !showUpload ? (
        <div className="w-full py-4 rounded-2xl border-2 border-dashed border-amber-500/30 bg-amber-500/5 text-amber-400 text-sm text-center">
          You've reached your {plan.name} plan's limit of {plan.maxFilesPerFolder} files in this folder.
          Delete a file, or upgrade your plan to add more.
        </div>
      ) : (
        <button
          onClick={() => { setShowUpload((v) => !v); if (showUpload) resetUpload(); }}
          className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl border-2 border-dashed border-primary/40 hover:border-primary bg-primary/5 hover:bg-primary/10 text-primary font-semibold text-sm transition mt-auto"
        >
          <span className="text-lg">＋</span>
          Add Content
        </button>
      )}

      {/* ── How to verify a file's Bitcoin timestamp ── */}
      <div className="mt-4 rounded-2xl border border-dark-border bg-dark-card/50 p-6 text-sm text-gray-400">
        <h3 className="font-semibold text-gray-200 text-lg">
          ₿ How to verify a file's Bitcoin timestamp
        </h3>
        <p className="mt-2 text-gray-500">
          Every uploaded file's hash is anchored with OpenTimestamps. New anchors take a
          few hours to gain a Bitcoin confirmation — after that, here's how to check one:
        </p>
        <ol className="mt-3 space-y-2 list-decimal list-inside">
          <li>
            <span className="text-gray-300 font-medium">In the app (fastest):</span> click{" "}
            <span className="text-amber-400">₿ Verify on Bitcoin</span> on any file.
          </li>
          <li>
            <span className="text-gray-300 font-medium">Independently:</span> download the
            file (View) and its proof (⇩ Download proof), then run{" "}
            <code className="text-gray-300">ots verify yourfile.ots</code> with the free{" "}
            <a
              href="https://github.com/opentimestamps/opentimestamps-client"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              ots CLI
            </a>
            .
          </li>
          <li>
            <span className="text-gray-300 font-medium">Zero third-party trust:</span> run
            the same command pointed at your own Bitcoin node instead of a public explorer
            (<code className="text-gray-300">ots verify --bitcoin-node ...</code>).
          </li>
          <li>
            <span className="text-gray-300 font-medium">No install:</span> drag the
            downloaded proof file into{" "}
            <a
              href="https://opentimestamps.org"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              opentimestamps.org
            </a>
            's web verifier.
          </li>
        </ol>
      </div>

      {/* ── What the Bitcoin timestamp proof actually is ── */}
      <div className="mt-4 rounded-2xl border border-dark-border bg-dark-card/50 p-6 text-sm text-gray-400">
        <h3 className="font-semibold text-gray-200 text-lg">
          🔒 Blockchain Timestamp Proof
        </h3>
        <p className="mt-2 text-gray-500">
          When you upload a file, ShadowVault automatically creates a
          tamper-evident timestamp for it using the Bitcoin blockchain.
        </p>

        <p className="mt-3 text-gray-300 font-medium">What this means for you:</p>
        <ul className="mt-2 space-y-2 list-disc list-inside">
          <li>
            The moment you upload a file, we record a{" "}
            <span className="text-gray-300">cryptographic fingerprint</span>{" "}
            of it (not the file itself) and submit it to be permanently
            stamped into the Bitcoin blockchain.
          </li>
          <li>
            That fingerprint is a one-way digital summary of the file's exact
            content — change even one bit of the file and the fingerprint
            comes out completely different. It can't be reversed back into
            the file, and it carries no metadata (no filename, no uploader,
            no date) — just a check on content, nothing else.
          </li>
          <li>
            This proves the file existed at that exact time, and that it
            hasn't been altered since — even ShadowVault couldn't secretly
            change it later without the proof breaking.
          </li>
          <li>
            It's the same idea as a notary stamping a document with today's
            date — except instead of trusting one notary, it's backed by the
            entire Bitcoin network, which nobody controls or can rewrite.
          </li>
        </ul>

        <p className="mt-3 text-gray-300 font-medium">Why it takes a little time:</p>
        <ul className="mt-2 space-y-2 list-disc list-inside">
          <li>
            The proof isn't instant. It first gets a "pending" timestamp
            (usually within minutes), and then gets permanently locked into
            an actual Bitcoin block within a few hours.
          </li>
          <li>
            Until that happens, your file shows{" "}
            <span className="text-gray-300">"Pending Bitcoin"</span> in the
            app. Once it's locked in, you'll see a{" "}
            <span className="text-amber-400">"Verify on Bitcoin"</span>{" "}
            button.
          </li>
        </ul>

        <p className="mt-3 text-gray-300 font-medium">What you can do with it:</p>
        <ul className="mt-2 space-y-2 list-disc list-inside">
          <li>
            Click <span className="text-amber-400">"Verify on Bitcoin"</span>{" "}
            any time to confirm the file hasn't been tampered with since
            upload.
          </li>
          <li>
            Download the proof file if you ever need to show someone outside
            ShadowVault (a court, an auditor, another service) independent
            evidence of when this file existed — it doesn't depend on
            trusting ShadowVault's word.
          </li>
        </ul>
      </div>
    </div>
  );
}
